import React from 'react';
import styled, { css } from 'styled-components';

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';
export type CardElevation = 'none' | 'sm' | 'md' | 'lg';

type StyledCardProps = {
  $padding: CardPadding;
  $elevation: CardElevation;
  $interactive: boolean;
};

const paddingStyles: Record<CardPadding, string> = {
  none: '0',
  sm: 'var(--space-3)',
  md: 'var(--space-5)',
  lg: 'var(--space-6)',
};

const elevationStyles: Record<CardElevation, string> = {
  none: 'var(--shadow-none)',
  sm: 'var(--shadow-xs)',
  md: 'var(--shadow-sm)',
  lg: 'var(--shadow-md)',
};

const StyledCard = styled.div<StyledCardProps>`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: ${(p) => paddingStyles[p.$padding]};
  box-shadow: ${(p) => elevationStyles[p.$elevation]};
  transition:
    box-shadow var(--duration-base) var(--ease-standard),
    border-color var(--duration-base) var(--ease-standard),
    transform var(--duration-base) var(--ease-standard);

  ${(p) =>
    p.$interactive &&
    css`
      cursor: pointer;
      &:hover {
        box-shadow: var(--shadow-md);
        border-color: var(--color-border-strong);
        transform: translateY(-1px);
      }
      &:active {
        transform: translateY(0);
      }
    `}
`;

export type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  padding?: CardPadding;
  elevation?: CardElevation;
  interactive?: boolean;
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      padding = 'md',
      elevation = 'sm',
      interactive = false,
      children,
      ...rest
    },
    ref
  ) => {
    return (
      <StyledCard
        ref={ref}
        $padding={padding}
        $elevation={elevation}
        $interactive={interactive}
        {...rest}
      >
        {children}
      </StyledCard>
    );
  }
);

Card.displayName = 'Card';
